/**
 * NewLeadModal — recruiter-facing "New lead" create flow (punch-list item 9).
 *
 * Every other contact is born from the public WCR funnel; this is the only
 * authenticated create path, for a patient on the phone with no prior web
 * submission. POSTs /api/crm/contacts, which routes through crm.resolve_contact
 * (so it inherits dedup + the round-robin owner trigger) and stamps
 * origin='manual'.
 *
 * TCPA posture (owner-locked): a typed number has no web-form consent artifact,
 * so the "Send welcome text" checkbox is OFF by default — nothing is texted /
 * enrolled unless the recruiter explicitly opts in.
 *
 * If the phone/email matches an existing contact the backend LINKS rather than
 * creating a duplicate and returns { matched: true } — we surface that and offer
 * to open the existing record instead of silently making a near-duplicate.
 *
 * Mirrors EditContactModal's bottom-sheet styling. onCreated receives the new or
 * matched contact id so the parent can navigate to its detail page.
 */
import { useState } from 'react';
import { AlertTriangle, Loader2, UserPlus } from 'lucide-react';
import api from '../../services/api';
import { formatPhoneInput } from '../lib';
import { Contact, StudyOption, TeamMember, contactName, studyInternalLabel } from '../types';
import { Z } from './ui';

interface NewLeadModalProps {
  studies: StudyOption[];
  team: TeamMember[];
  onClose: () => void;
  /** Navigate to the (new or matched) contact's detail page. */
  onCreated: (contactId: string) => void;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

export default function NewLeadModal({ studies, team, onClose, onCreated }: NewLeadModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [studySlug, setStudySlug] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [sendWelcome, setSendWelcome] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the backend links to an existing patient instead of creating one.
  // The post-create confirmation state: a dedup match, a randomized-elsewhere
  // warning, or both. Either one stops the auto-navigate so the recruiter sees it.
  const [matched, setMatched] = useState<{
    id: string; name: string; randomizedIn?: string | null; isMatch?: boolean;
  } | null>(null);

  // Only studies backed by a crm.studies row (id set) resolve on the server;
  // lead-only stubs would 400. Hide archived studies.
  const studyOptions = studies
    .filter((s) => s.id && !s.archived_at)
    .sort((a, b) => studyInternalLabel(a).localeCompare(studyInternalLabel(b)));

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/contacts', {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        phone: phone.trim(),
        email: email.trim() || null,
        study_slug: studySlug || undefined,
        owner_user_id: ownerUserId || undefined,
        send_welcome: sendWelcome,
      });
      const contact: Contact | undefined = res.data?.contact;
      if (!contact?.id) throw new Error('No contact returned');
      // Multi-study lead lists: creating a lead is never BLOCKED by the
      // randomized guard (the person already exists), so a conflict rides the
      // response as a warning instead. Shown in the matched state, which is
      // where a returning patient lands.
      const conflictWarning = res.data?.randomized_conflict
        ? (res.data.randomized_conflict.study?.title || 'another study')
        : null;
      if (res.data?.matched || conflictWarning) {
        // Linked to an existing patient, or already randomized elsewhere —
        // don't silently navigate; let the recruiter confirm and open the record.
        setMatched({
          id: contact.id,
          name: contactName(contact) || 'this patient',
          randomizedIn: conflictWarning,
          isMatch: Boolean(res.data?.matched),
        });
        setSaving(false);
        return;
      }
      onCreated(contact.id);
    } catch (e: any) {
      const apiErr = e?.response?.data?.error;
      setError(apiErr || e?.response?.data?.detail || e.message || 'Could not create the lead.');
      setSaving(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={saving ? undefined : onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New lead"
      >
        <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <UserPlus className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          New lead
        </h3>

        {matched ? (
          // ---- Matched-existing state -------------------------------------
          <>
            {matched.isMatch && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                This matches an existing patient (<span className="font-medium">{matched.name}</span>). We linked
                this entry to their record instead of creating a duplicate.
              </p>
            )}
            {matched.randomizedIn && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <span className="font-medium">{matched.name}</span> is currently randomized in{' '}
                  <span className="font-semibold">{matched.randomizedIn}</span>. They were still added to this
                  study&rsquo;s list — check before working them.
                </span>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => onCreated(matched.id)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
              >
                Open their record
              </button>
            </div>
          </>
        ) : (
          // ---- Create form ------------------------------------------------
          <>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Enter a patient you spoke with directly. A phone number or email is required.
            </p>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>First name</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} autoFocus />
                </div>
                <div>
                  <label className={labelCls}>Last name</label>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  placeholder="123-456-7890"
                  inputMode="tel"
                  autoComplete="tel"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  inputMode="email"
                  autoComplete="email"
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Study (optional)</label>
                  <select value={studySlug} onChange={(e) => setStudySlug(e.target.value)} className={inputCls}>
                    <option value="">No study</option>
                    {studyOptions.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {studyInternalLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Owner (optional)</label>
                  <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} className={inputCls}>
                    <option value="">Auto-assign</option>
                    {team.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <input
                  type="checkbox"
                  checked={sendWelcome}
                  onChange={(e) => setSendWelcome(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  <span className="font-medium text-gray-800 dark:text-gray-100">Send welcome text</span>
                  <br />
                  Only tick this if the patient agreed to be texted. Off by default — a typed number has no
                  web-form consent on file.
                </span>
              </label>
            </div>

            {error && (
              <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Creating…' : 'Create lead'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
